import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';

import { ToastrService } from 'ngx-toastr';
import { map, catchError, concatMap } from 'rxjs/operators';

import { JumpstoryActions } from '../actions';
import { EmailService } from '../services';

@Injectable()
export class JumpstoryStatisticsEffects {
  fetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JumpstoryActions.fetchRequest),
      concatMap(({ timeSpans }) => {
        const dateRange = this.getDateRange(timeSpans);
        return this.emailService.fetchJumpstoryStatistics(dateRange).pipe(
          map((jumpstoryStats) =>
            JumpstoryActions.fetchSuccess({ jumpstoryStats }),
          ),
          catchError(() => {
            this.toastr.error('Could not fetch jumpstory statistics');
            return [JumpstoryActions.fetchFailure()];
          }),
        );
      }),
    ),
  );

  fetchByAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JumpstoryActions.fetchByAccountRequest),
      concatMap(({ accountId, timeSpans }) => {
        const dateRange = this.getDateRange(timeSpans);
        return this.emailService
          .fetchJumpstoryStatisticsByAccount(accountId, dateRange)
          .pipe(
            map((jumpstoryStats) =>
              JumpstoryActions.fetchSuccess({ jumpstoryStats }),
            ),
            catchError(() => {
              this.toastr.error('Could not fetch jumpstory statistics');
              return [JumpstoryActions.fetchFailure()];
            }),
          );
      }),
    ),
  );

  downloadCSV = createEffect(() =>
    this.actions$.pipe(
      ofType(JumpstoryActions.downloadCSVRequest),
      concatMap(({ accountId, timeSpans }) => {
        const dateRange = this.getDateRange(timeSpans);
        return this.emailService
          .getJumpstoryStatisticsCSV(accountId, dateRange)
          .pipe(
            map((csv) => {
              this.download(csv);
              return JumpstoryActions.downloadCSVSuccess();
            }),
            catchError((error) => {
              this.toastr.error('Could not download CSV', 'Request failed');
              return of(JumpstoryActions.downloadCSVFailure(error));
            }),
          );
      }),
    ),
  );

  download(file: ArrayBuffer) {
    const blob = new Blob([file], { type: 'text/csv;charset=utf-8;' });
    const anchorEl = document.createElement('a');
    const fileName = `${Date.now()}.csv`;
    document.body.appendChild(anchorEl);

    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, fileName);
    } else {
      const url = window.URL.createObjectURL(blob);
      anchorEl.href = url;
      anchorEl.download = fileName;
      anchorEl.click();
      window.URL.revokeObjectURL(url);
    }

    document.body.removeChild(anchorEl);
  }

  getDateRange(timeSpans: number[]) {
    return {
      from: timeSpans[0],
      to: timeSpans[timeSpans.length - 1],
    };
  }

  constructor(
    private readonly actions$: Actions,
    private readonly emailService: EmailService,
    private readonly toastr: ToastrService,
  ) {}
}
